/* The answers a task collected, as a table, and the approve/reject workflow over them.
 *
 * MOUNTED ONLY WHILE OPEN, and that is not negotiable (spec/03#mount-one-section-at-a-time). A task
 * can hold dozens of answers, each with several photos, so rendering them alongside the form would
 * put a request and an unbounded image list on every page open. `Page.js` fetches on first open and
 * caches; switching away unmounts this — not `display: none`, actually unmounted, which is what
 * `document.querySelectorAll(".tk-answers").length` asserts.
 *
 * Built from the SHARED table parts so it reads as the same product as every other table in the
 * app: `PersonCell` / `actionsColumn` / `RowActions` / `dash` from `component/table-cells.js`, the
 * `lp-table__grid` skin from `component/list-page.scss` (header type, body type, hover) and its
 * `lp-main` / `lp-main__sub` / `lp-num` cell classes — all imported once from `src/index.js`.
 *
 * ⚠ `visit/sections.js`'s `SectionTable` is deliberately NOT reused: it is a local function styled
 * from `vd-table` in `visit-detail.scss`, so importing it would mean importing another page's whole
 * stylesheet to get a border and a font size. The verdict logic IS shared from `visit/parts.js`
 * (`statusOf`, `StatusTag`, `postApproval`, `RejectModal`) — those are CSS-independent, because the
 * pill's shape comes from `.status-tag` in `theme/antd.scss`, which is global.
 */

import React, { useMemo, useState } from "react";
import { Alert as AntAlert, Button, Empty, Flex, Image, Skeleton, Space, Table, Tag, Typography } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";

import Alert from "/src/util/alert";

import { PersonCell, RowActions, actionsColumn, dash } from "../../component/table-cells";
import { TASK_DETAIL } from "../../config/lang";
import { translate } from "../../util/util";
import { RejectModal, StatusTag, postApproval, statusOf } from "../visit/parts";

/** Every place a photo can live on an answer: the legacy single field, and the AnswerPhoto rows the
    mobile app writes. Both, or a multi-photo answer renders as having none. */
const photosOf = answer => {
  const many = (answer.photos || []).map(photo => photo.photo_url).filter(Boolean);
  if (many.length) return many;
  return answer.photo ? [answer.photo] : [];
};

export default function Answers({ task, rows, lang, dateFmt, onModerated }) {
  const t = key => translate(TASK_DETAIL, key, lang);
  const [busy, setBusy] = useState(null);
  const [rejecting, setRejecting] = useState(null);

  const decide = (answer, isApproved, comment = "") => {
    setBusy(answer.id);
    postApproval({ objectId: answer.id, objectType: "answer", isApproved, comment }, ok => {
      setBusy(null);
      setRejecting(null);
      if (!ok) {
        Alert.error(t("load_error"), { position: "top-right" });
        return;
      }
      Alert.success(t(isApproved ? "approved_msg" : "rejected_msg"), { position: "top-right" });
      /* Only this section and the task's own status refetch — anything that repaints the whole page
         loses the reviewer's place. */
      onModerated();
    });
  };

  const columns = useMemo(
    () => [
      /* Written out rather than built from `statusColumn()`: that helper is shaped for a 44px
         icon-only glyph and this column wants a labelled pill — a reviewer is deciding about the
         word, and there is room for it inside one task's answers. Overriding four of its six
         properties to claim reuse would be worse than not using it. The key and `fixed: "left"`
         match it so the column sits where every other table's status sits. */
      {
        key: "status",
        title: t("col_status"),
        width: 150,
        fixed: "left",
        render: (value, row) => <StatusTag status={statusOf(row.approval)} lang={lang} />,
      },
      {
        key: "photos",
        title: t("k_photos"),
        width: 90,
        render: (value, row) => {
          const photos = photosOf(row);
          if (!photos.length) return dash;
          /* One thumbnail opens the whole set — antd's preview is the viewer, which is also what
             replaced `react-image-lightbox`, whose prev and next both stepped the same way. */
          return (
            <Image.PreviewGroup>
              <Image src={photos[0]} width={44} height={44} className="tk-thumb" alt="" />
              {photos.slice(1).map(url => (
                <Image key={url} src={url} style={{ display: "none" }} alt="" />
              ))}
              {photos.length > 1 ? <span className="tk-thumb__n lp-num">{photos.length}</span> : null}
            </Image.PreviewGroup>
          );
        },
      },
      {
        key: "answer",
        title: t("ans_text"),
        ellipsis: true,
        render: (value, row) => {
          const tags = row.selected_tags || [];
          if (!row.answer && !tags.length) return dash;
          return (
            <Flex vertical className="lp-main">
              <Typography.Text ellipsis={{ tooltip: row.answer }}>{row.answer || dash}</Typography.Text>
              {tags.length ? (
                <Space size={4} wrap className="lp-main__sub">
                  {tags.map((tag, index) => (
                    <Tag key={`${tag}-${index}`} className="tk-tag--quiet">
                      {tag}
                    </Tag>
                  ))}
                </Space>
              ) : null}
            </Flex>
          );
        },
      },
      {
        key: "boolean",
        title: t("cmp_boolean"),
        width: 90,
        /* A yes/no answer is a word, never a colour alone. */
        render: (value, row) =>
          row.boolean_answer === null || row.boolean_answer === undefined ? (
            dash
          ) : (
            <Typography.Text strong>{t(row.boolean_answer ? "ans_yes" : "ans_no")}</Typography.Text>
          ),
      },
      { key: "by", title: t("ans_by"), width: 180, ellipsis: true, render: (value, row) => <PersonCell user={row.visit?.user} strong={false} /> },
      { key: "created", title: t("ans_date"), width: 170, render: (value, row) => (row.created ? dateFmt.format(new Date(row.created)) : dash) },
      /* A row action is always a menu, in the same place and shape as every other table's, so a
         second action later does not move the first. Offered only while the answer is still
         awaiting a verdict — re-deciding is a different operation, and the old page's menu offered
         Approve and Reject on every row regardless of what had already been decided. */
      actionsColumn((value, row) =>
        statusOf(row.approval) !== "pending" ? null : (
          <RowActions
            label={t("row_actions")}
            items={[
              { key: "approve", icon: <CheckCircleOutlined />, label: t("approve") },
              { key: "reject", icon: <CloseCircleOutlined />, label: t("reject"), danger: true },
            ]}
            onClick={key => (key === "approve" ? decide(row, true) : setRejecting(row))}
          />
        )
      ),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lang, dateFmt, busy]
  );

  /* `undefined` is "not fetched yet", `null` is "the fetch failed" — different states needing
     different UI (spec/03). An empty array is a real, successful, empty answer list. */
  if (rows === undefined) return <Skeleton active paragraph={{ rows: 4 }} />;
  if (rows === null) {
    return (
      <AntAlert
        type="error"
        showIcon
        message={t("load_error")}
        action={
          <Button size="small" onClick={() => onModerated()}>
            {t("retry")}
          </Button>
        }
      />
    );
  }

  return (
    <div className="tk-answers">
      <Table
        className="lp-table__grid tk-anstable"
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={rows}
        pagination={false}
        scroll={{ x: 940 }}
        loading={busy !== null}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("ans_empty")} /> }}
      />

      {/* Reject ASKS FOR A REASON. The legacy flow posted `comment: ""` every time, so a rejected
          answer could never say why — the one thing the merchandiser needs in order to fix it. */}
      <RejectModal
        open={Boolean(rejecting)}
        target={{ title: task?.title }}
        lang={lang}
        onCancel={() => setRejecting(null)}
        onConfirm={reason => decide(rejecting, false, reason)}
      />
    </div>
  );
}
